import { createElement, isValidElement } from 'react';
import './NavItem.css';

/**
 * NavItem — React port of Figma's `Nav / Item`.
 *
 * ─── Figma variant axis → React prop ──────────────────────────────────────────
 *   Layout    'Stacked' | 'Row' | 'Rail'   → `layout`     ('stacked'|'row'|'rail')
 *   Selected  true | false                 → `selected`
 *   ShowLabel true | false                 → `showLabel`  (forced false on 'rail')
 *   Label     <text>                       → `label`
 *   Icon      <instance swap>              → `icon`
 *   Badge     none | dot | count           → `badge`      (true|'dot' → 8px dot;
 *                                                         any other truthy value → 16px
 *                                                         count pill; 0/null → nothing)
 *
 *   State (Default / Hover / Pressed / Focus / Disabled) is NOT a prop — it is
 *   expressed with CSS pseudo-classes (:hover, :active, :focus-visible,
 *   :disabled). Only `disabled` crosses over, because it is a real DOM concern.
 *
 * ─── Geometry (all from nav tokens, nothing hardcoded) ────────────────────────
 *   row     — 28 tall, padding 4/10, gap 8, icon 20. Full pill
 *             (--nav-active-radius) in every state, so hover and selected are
 *             the same shape.
 *   rail    — 28x28 square, icon centred, never renders a label. Same
 *             --nav-active-radius in every state — a full pill on a square box,
 *             i.e. a true circle.
 *   stacked — the mobile Floating bar item: 76 tall, padding 10, gap 8, icon 24,
 *             radius --nav-mobile-active-radius in every state.
 *
 *   The Figma spec pins the icon to x=18 in both sidebar states. This component
 *   supplies the item half of that sum: row contributes its 10px inline padding,
 *   rail contributes the 4px that centring a 20px icon in a 28px box produces.
 *   The remaining 8 (menu padding) + 6 (rail inset) come from the container.
 *
 * ─── Element semantics ────────────────────────────────────────────────────────
 *   Renders a real <button> by default, a real <a> when `href` is given, or any
 *   component passed as `as` — e.g. react-router's NavLink:
 *
 *     <NavItem as={NavLink} to="/focus" layout="row" icon={Target} label="Focus"
 *              selected={isActive} badge={3} />
 *
 *   `icon` accepts a component (lucide's `Target`) or an already-built node
 *   (`<IconRenderer … />`, an emoji string). Size is enforced in CSS, so any of
 *   those render at the right size without the caller passing one.
 *
 *   A rail item (or any item with its label hidden) gets `aria-label` from
 *   `label`, so it still has an accessible name. It renders NO tooltip of its
 *   own: an absolutely-positioned one cannot escape a scrolling/clipping rail,
 *   so a visible hover hint is the portal `Tooltip` primitive's job and the
 *   caller wraps the item in it.
 *
 * NOTE: this is a primitive. It does not know about routes, stores or layout —
 * screens compose it.
 */
export default function NavItem({
    layout = 'row',
    selected = false,
    showLabel = layout !== 'rail',
    label,
    icon,
    badge = null,
    // Accepted and ignored. NavItem no longer renders a tooltip (see the
    // header note); destructuring it here keeps a legacy `tooltip={false}`
    // from spreading onto the DOM node as a stray attribute.
    // eslint-disable-next-line no-unused-vars
    tooltip,
    disabled = false,
    as,
    href,
    onClick,
    className = '',
    children,
    ...rest
}) {
    const Component = as || (href ? 'a' : 'button');
    const isNativeButton = Component === 'button';

    // Rail never renders a label; every other layout honours `showLabel`.
    const labelVisible = layout !== 'rail' && showLabel !== false && label != null && label !== '';

    // `badge={0}` renders nothing, so callers can pass a raw count straight through.
    const isDot = badge === true || badge === 'dot';
    const hasCount = !isDot
        && badge !== null && badge !== undefined && badge !== false && badge !== '' && badge !== 0;
    const badgeNode = (isDot || hasCount) ? (
        <span
            className={`nav-item__badge${isDot ? ' nav-item__badge--dot' : ''}`}
            aria-hidden={isDot ? 'true' : undefined}
        >
            {hasCount ? badge : null}
        </span>
    ) : null;

    const classes = [
        'nav-item',
        `nav-item--${layout}`,
        selected ? 'nav-item--selected' : '',
        disabled ? 'nav-item--disabled' : '',
        className,
    ].filter(Boolean).join(' ');

    // aria-current marks the selected destination; callers may override with a
    // different token ('step', 'true', …) through `rest`.
    const ariaCurrent = 'aria-current' in rest ? rest['aria-current'] : (selected ? 'page' : undefined);

    const elementProps = {
        ...rest,
        className: classes,
        onClick: disabled ? undefined : onClick,
        'aria-label': rest['aria-label'] ?? (!labelVisible && typeof label === 'string' ? label : undefined),
    };

    // The key must be ABSENT, not `undefined`, when the item is unselected.
    // Wrappers that own an aria-current default — react-router's NavLink is the
    // one that bit us — read a literal `undefined` as "no prop passed, use my
    // default" and then apply "page" from their own pathname match, which
    // ignores the query string. At /reviews?tab=weekly that produced three rows
    // claiming aria-current="page", two of them contradicting the visual state.
    if (ariaCurrent !== undefined) elementProps['aria-current'] = ariaCurrent;
    else delete elementProps['aria-current'];

    if (isNativeButton) {
        elementProps.type = rest.type || 'button';
        elementProps.disabled = disabled;
    } else {
        if (href) elementProps.href = disabled ? undefined : href;
        if (disabled) {
            elementProps['aria-disabled'] = 'true';
            elementProps.tabIndex = -1;
        }
    }

    return createElement(
        Component,
        elementProps,
        <span className="nav-item__icon">
            {renderIcon(icon)}
            {layout === 'row' ? null : badgeNode}
        </span>,
        labelVisible ? <span className="nav-item__label">{label}</span> : null,
        layout === 'row' ? badgeNode : null,
        children,
    );
}

/** Accepts a component type (lucide icon) or a ready-made node. */
function renderIcon(icon) {
    if (icon === null || icon === undefined || icon === false) return null;
    if (isValidElement(icon)) return icon;
    if (typeof icon === 'function' || (typeof icon === 'object' && icon.$$typeof)) {
        return createElement(icon, { 'aria-hidden': 'true', focusable: 'false' });
    }
    return icon; // emoji / plain string
}
